import { DEFAULT_CONFIG } from '../constants';

export class DidService {
  constructor(apiKey = DEFAULT_CONFIG.didKey, apiUrl = DEFAULT_CONFIG.didUrl) {
    this.apiKey = apiKey;
    this.apiUrl = apiUrl;
    this.streamId = null;
    this.sessionId = null;
    this.peerConnection = null;
  }

  getAuthHeader() {
    return `Basic ${btoa(this.apiKey + ':')}`;
  }

  isConnected() {
    return Boolean(
      this.streamId &&
      this.sessionId &&
      this.peerConnection &&
      this.peerConnection.connectionState !== 'closed' &&
      this.peerConnection.connectionState !== 'failed'
    );
  }

  async createStream(avatarUrl) {
    if (!avatarUrl) {
      throw new Error('Please select or upload an avatar image before connecting.');
    }

    const response = await fetch(`${this.apiUrl}/talks/streams`, {
      method: 'POST',
      headers: {
        'Authorization': this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        source_url: avatarUrl,
        stream_warmup: false,
        config: { video_quality: 'hd' },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`D-ID stream creation failed (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    this.streamId = data.id;
    this.sessionId = data.session_id;

    return data;
  }

  async createPeerConnection(offer, iceServers, onTrack, onIceConnectionStateChange) {
    const PeerConnection = window.RTCPeerConnection || window.webkitRTCPeerConnection || window.mozRTCPeerConnection;
    if (!PeerConnection) {
      throw new Error('WebRTC is not supported in this browser.');
    }

    this.peerConnection = new PeerConnection({ iceServers });

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.streamId) {
        fetch(`${this.apiUrl}/talks/streams/${this.streamId}/ice`, {
          method: 'POST',
          headers: {
            'Authorization': this.getAuthHeader(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
            session_id: this.sessionId,
          }),
        }).catch(err => console.warn('[D-ID] ICE exchange warning:', err));
      }
    };

    if (onIceConnectionStateChange) {
      this.peerConnection.oniceconnectionstatechange = () => {
        onIceConnectionStateChange(this.peerConnection.iceConnectionState);
      };
    }

    if (onTrack) {
      this.peerConnection.ontrack = (event) => {
        onTrack(event);
      };
    }

    await this.peerConnection.setRemoteDescription(offer);
    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);

    return answer;
  }

  async sendSDPAnswer(answer) {
    if (!this.streamId || !this.sessionId) {
      throw new Error('Cannot send SDP answer: Missing active session or stream ID');
    }

    const response = await fetch(`${this.apiUrl}/talks/streams/${this.streamId}/sdp`, {
      method: 'POST',
      headers: {
        'Authorization': this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        answer,
        session_id: this.sessionId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`SDP answer failed (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  async sendTalkRequest(aiText, voiceId = DEFAULT_CONFIG.defaultVoice) {
    if (!this.streamId || !this.sessionId) {
      throw new Error('Cannot talk: Stream is not connected.');
    }

    const response = await fetch(`${this.apiUrl}/talks/streams/${this.streamId}`, {
      method: 'POST',
      headers: {
        'Authorization': this.getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        script: {
          type: 'text',
          input: aiText,
          provider: {
            type: 'microsoft',
            voice_id: voiceId,
            voice_config: { style: 'Cheerful' },
          },
        },
        config: { fluent: true, stitch: true },
        driver_url: 'bank://lively/',
        session_id: this.sessionId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`D-ID talk request failed: ${errorText}`);
    }

    return response.json();
  }

  async deleteStream() {
    if (!this.streamId || !this.sessionId) return;

    try {
      await fetch(`${this.apiUrl}/talks/streams/${this.streamId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': this.getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: this.sessionId }),
      });
    } catch (err) {
      console.warn('[D-ID] Error deleting stream:', err);
    } finally {
      this.streamId = null;
      this.sessionId = null;
    }
  }

  cleanup() {
    if (this.peerConnection) {
      try {
        this.peerConnection.close();
      } catch (e) {}
      this.peerConnection = null;
    }
    this.streamId = null;
    this.sessionId = null;
  }
}
